/**
 * Lottery Service for Frontend User-Classic
 * Service for fetching lottery rounds and information
 */

import { getOptionalTenantId } from '../utils/tenantUtils'
import { getApiBaseUrl } from '../lib/apiBaseUrl'

type ParsedResponseBody = {
    data: unknown
    text: string
}

async function parseResponseBody(response: Response): Promise<ParsedResponseBody> {
    const text = await response.text().catch(() => '')
    if (!text) {
        return { data: null, text: '' }
    }

    try {
        return {
            data: JSON.parse(text),
            text
        }
    } catch {
        return {
            data: null,
            text
        }
    }
}

function resolvePlaceBetErrorMessage(response: Response, body: ParsedResponseBody): string {
    const status = Number(response.status || 0)
    const payloadMessage = typeof (body.data as { message?: unknown } | null)?.message === 'string'
        ? String((body.data as { message?: string }).message || '').trim()
        : ''
    const rawText = String(body.text || '').trim()
    const contentType = String(response.headers.get('content-type') || '').toLowerCase()
    const looksLikeHtml = contentType.includes('text/html') || rawText.startsWith('<!doctype') || rawText.startsWith('<html')

    if ([502, 503, 504].includes(status) || looksLikeHtml) {
        return 'ระบบรับแทงขัดข้องชั่วคราว กรุณาลองใหม่อีกครั้ง'
    }

    return payloadMessage || `Failed to place bet (HTTP ${status || 'unknown'})`
}

// Lottery Round interfaces
export interface LotteryPrizeType {
    _id?: string
    code: string
    name: string
    order?: number
    displayOrder?: number
    customOdds?: number | null
    customMinBet?: number | null
    customMaxBet?: number | null
    minBet?: number | null
    maxBet?: number | null
    isEnabled?: boolean
    source?: string | null
    [key: string]: any
}

export interface LotteryPrizeSettingTier {
    minTotalBet: number
    maxTotalBet?: number | null
    payoutAmount?: number | null
    payoutPercent?: number | null
}

export interface LotteryPrizeConfig {
    prizeTypeCode: string
    payoutRate: number
    payoutTier?: string
    minBet?: number | null
    maxBet?: number | null
    roundLimit?: number | null
    transactionLimit?: number | null
    numberLimit?: number | null
    isActive?: boolean
    displayOrder?: number
    metadata?: Record<string, any>
    payoutTiers?: LotteryPrizeSettingTier[]
}

export interface LotteryPrizeSettingPrize {
    _id?: string
    prizeTypeId: string | null
    prizeTypeCode?: string
    prizeTypeName?: string
    prizeAmount: number
    prizeLimitAmount?: number | null
    minTotalReceive?: number | null
    maxTotalReceive?: number | null
    minBet?: number | null
    maxBet?: number | null
    maxNumberPerPrizeType?: number | null
    isActive?: boolean
    payoutTiers?: LotteryPrizeSettingTier[]
}

export interface LotteryPrizeSettings {
    _id: string | null
    huayId?: string | null
    huayTypeId?: string | null
    huayName?: string
    isActive?: boolean
    prizes: LotteryPrizeSettingPrize[]
    createdAt?: string
    updatedAt?: string
}

export interface LotteryPrizeGroup {
    _id: string
    code: string
    name: string
    description?: string
    source?: string | null
    configuration?: Record<string, any>
    vendorSettings?: Record<string, any>
    prizeTypes: LotteryPrizeType[]
    prizeConfigs?: LotteryPrizeConfig[]
    prizes?: LotteryPrizeSettingPrize[]
}

export interface LotteryRound {
    _id: string
    huayId: string
    huayName: string
    huayCode?: string
    huayImageURL?: string | null
    huayTypeId: string
    huayTypeName: string
    huayTypeCode?: string
    huayTypeIcon?: string | null
    roundNumber: string
    status: 'pending' | 'open' | 'closed' | 'announced' | 'cancelled'
    isOpen: boolean
    betStartTime: string
    betEndTime: string
    resultAnnounceTime: string
    drawDate: string
    drawTime: string
    result?: string
    totalBets?: number
    totalAmount?: number
    createdAt: string
    updatedAt: string
    prizeGroupId?: string | null
    prizeGroup?: LotteryPrizeGroup | null
    customPrizes?: Array<Record<string, any>>
    prizeSettings?: LotteryPrizeSettings | null
    yeekeeConfig?: {
        numberDigits?: number
    } | null
    roundLimits?: {
        prizeTypeLimits?: Array<{
            prizeTypeCode: string
            closedNumbers?: string[]
            isActive?: boolean
            numberSettings?: Array<{
                number: string
                customPayoutRate?: number | null
                isFull?: boolean
                isRestricted?: boolean
                reverseNumber?: boolean
                notes?: string | null
                setBy?: string
                setAt?: string
            }>
        }>
    }
    /** Non-Yeekee: รางวันที่ 1, 3ตัวบน, 2ตัวล่าง, 3ตัวล่าง; หวยชุด: fourUp, twoUp (2 ตัวหน้า/หลัง) */
    results?: {
        winningNumbers?: {
            first?: string
            threeUp?: string
            twoDown?: string
            threeDown?: string
            fourUp?: string
            twoUp?: string
            [key: string]: string | undefined
        }
    } | null
    yeekee?: {
        totalShots: number
        shotCount: number
        result?: {
            sumTotal: string
            finalNumber: string
            threeDigitTop: string
            twoDigitTop: string
            twoDigitDown?: string
        }
    }
}

export interface PublicHuayType {
    _id: string
    huayTypeId?: string
    name: string
    code?: string
    imageUrl?: string | null
    imageURL?: string | null
    configuration?: { isActive?: boolean }
}

function normalizeHuayLabel(value: string): string {
    return String(value || '')
        .trim()
        .replace(/\s+/g, '')
        .replace(/[().]/g, '')
        .toLowerCase()
}

export interface LotteryRoundsResponse {
    success: boolean
    data: LotteryRound[]
    error?: string
}

const CLOSED_FOR_BETTING_STATUSES = new Set([
    'closed',
    'cancelled',
    'announced',
    'completed',
    'processing',
    'settling',
    'settled',
    'suspended'
])

export function canRoundAcceptBet(round: Pick<LotteryRound, 'status' | 'betEndTime'>): boolean {
    const status = String(round.status || '').toLowerCase()
    if (CLOSED_FOR_BETTING_STATUSES.has(status)) {
        return false
    }

    const betEndTime = new Date(round.betEndTime).getTime()
    if (!Number.isFinite(betEndTime)) {
        return false
    }

    return betEndTime > Date.now()
}

/**
 * รอบที่ "ยังไม่เปิดรับแทง" — สถานะยังไม่ปิด แต่ยังไม่ถึงเวลาเปิด (betStartTime ในอนาคต)
 * ใช้แยกการ์ดให้ขึ้นสีเทาว่ารอเวลาเปิด แทนที่จะกดได้แล้วโดนปฏิเสธว่า "ปิดรับแทงแล้ว"
 */
export function isRoundNotYetOpen(round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>): boolean {
    const status = String(round.status || '').toLowerCase()
    if (CLOSED_FOR_BETTING_STATUSES.has(status)) {
        return false
    }

    const betStartTime = new Date(round.betStartTime).getTime()
    if (!Number.isFinite(betStartTime)) {
        return false
    }

    // ถ้าเลยเวลาปิดไปแล้ว ถือว่าปิด ไม่ใช่ "ยังไม่เปิด"
    const betEndTime = new Date(round.betEndTime).getTime()
    if (Number.isFinite(betEndTime) && betEndTime <= Date.now()) {
        return false
    }

    return betStartTime > Date.now()
}

/** ลำดับกลุ่มบนหน้า /lotto: เปิดรับ → ยังไม่เปิด → ปิดแล้ว */
export function getLottoRoundDisplayRank(
    round: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>,
): number {
    if (isRoundNotYetOpen(round)) return 1
    return canRoundAcceptBet(round) ? 0 : 2
}

/** เรียงรอบหวยบนหน้า /lotto — เปิดรับเรียงตามเวลาปิดรับ (เร็ว → ช้า), ปิดแล้วไปท้าย */
export function compareRoundsForLottoDisplay(
    a: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>,
    b: Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>,
): number {
    const rankA = getLottoRoundDisplayRank(a)
    const rankB = getLottoRoundDisplayRank(b)
    if (rankA !== rankB) return rankA - rankB

    if (rankA === 0) {
        const endA = new Date(a.betEndTime).getTime()
        const endB = new Date(b.betEndTime).getTime()
        if (Number.isFinite(endA) && Number.isFinite(endB) && endA !== endB) {
            return endA - endB
        }
    } else if (rankA === 1) {
        const startA = new Date(a.betStartTime).getTime()
        const startB = new Date(b.betStartTime).getTime()
        if (Number.isFinite(startA) && Number.isFinite(startB) && startA !== startB) {
            return startA - startB
        }
    }

    return 0
}

export function sortRoundsForLottoDisplay<T extends Pick<LotteryRound, 'status' | 'betStartTime' | 'betEndTime'>>(
    rounds: T[],
): T[] {
    return [...rounds].sort(compareRoundsForLottoDisplay)
}

export function getRoundHuayTypeImage(
    round: Partial<LotteryRound> | null | undefined,
    huayTypes: PublicHuayType[]
): string | null {
    if (!round) return null

    const explicitImage = [
        (round as any).huayTypeImageURL,
        (round as any).huayTypeImage,
        (round as any).huayTypeIcon,
        (round as any).huayImageURL,
        (round as any).huayImage,
        (round as any).huayIcon
    ].find((value) => typeof value === 'string' && value.trim() !== '')

    if (typeof explicitImage === 'string' && explicitImage.trim() !== '') {
        return explicitImage
    }

    const roundTypeId = String(round.huayTypeId || '').trim()
    const roundTypeName = normalizeHuayLabel(String(round.huayTypeName || ''))
    const roundTypeCode = normalizeHuayLabel(String(round.huayTypeCode || ''))
    const roundHuayName = normalizeHuayLabel(String(round.huayName || ''))

    const matchedHuayType = huayTypes.find((huayType) => {
        const huayTypeId = String(huayType.huayTypeId || huayType._id || '').trim()
        const huayTypeName = normalizeHuayLabel(String(huayType.name || ''))
        const huayTypeCode = normalizeHuayLabel(String(huayType.code || ''))

        return (
            (!!roundTypeId && !!huayTypeId && roundTypeId === huayTypeId) ||
            (!!roundTypeCode && !!huayTypeCode && roundTypeCode === huayTypeCode) ||
            (!!roundTypeName && !!huayTypeName && (roundTypeName === huayTypeName || roundTypeName.includes(huayTypeName) || huayTypeName.includes(roundTypeName))) ||
            (!!roundHuayName && !!huayTypeName && (roundHuayName === huayTypeName || roundHuayName.includes(huayTypeName) || huayTypeName.includes(roundHuayName)))
        )
    })

    const image = String(matchedHuayType?.imageUrl || matchedHuayType?.imageURL || '').trim()
    return image || null
}

/** Bet slip item (รายการแทงในโพย) */
export interface BetSlipItem {
    prizeTypeCode: string
    prizeTypeName: string
    number: string
    amount: number
    lockedPayoutRate: number
    effectiveAmount: number
    potentialPayout: number
    status: string
    isWinner?: boolean | null
    winAmount?: number
}

/** Bet slip (โพย) from API */
export interface BetSlip {
    _id: string
    slipId: string
    tenantId: string
    userId: string
    username: string
    roundId: string
    huayId: string
    huayName: string
    roundNumber: string
    packageCode: string
    packageName?: string
    betItems: BetSlipItem[]
    totalAmount: number
    totalEffectiveAmount: number
    totalPotentialPayout: number
    totalWinAmount?: number
    refundAmount?: number
    refundedAt?: string | null
    status: string
    isSettled?: boolean
    settledAt?: string | null
    createdAt: string
    updatedAt?: string
    walletTransactionId?: string
    customerCancelWindowMinutes?: number
    [key: string]: any
}

export const CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES = 180
const CUSTOMER_SLIP_CANCEL_RULE_CODE = 'RULE_REFUND_SLIP_TIME'
let customerSlipCancelWindowCache = CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES
let customerSlipCancelWindowPromise: Promise<number> | null = null
const TENANT_RULES_CACHE_TTL_MS = 30_000
const tenantRulesCache = new Map<string, { at: number; data: TenantRuleContentItem[] }>()
const tenantRulesPromiseCache = new Map<string, Promise<TenantRuleContentItem[]>>()

export function formatCustomerSlipCancelWindowText(minutes: number): string {
    const normalizedMinutes = Math.max(1, Math.round(minutes || CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES))
    const wholeHours = normalizedMinutes / 60
    if (Number.isInteger(wholeHours)) {
        return `${wholeHours} ชม. หรือ ${normalizedMinutes} นาที`
    }
    return `${normalizedMinutes} นาที`
}

export interface TenantRuleContentItem {
    _id?: string
    code: string
    title: string
    content: string
    contentType?: 'text' | 'html' | 'markdown'
    category?: string
    isActive?: boolean
    displayOrder?: number
    priority?: number
    selectionMode?: 'all' | 'type' | 'huay'
    applyToAllHuayTypes?: boolean
    applyToAllHuays?: boolean
    relatedHuayTypes?: Array<string | { _id?: string; code?: string; name?: string; nameTh?: string }>
    relatedHuays?: Array<string | { _id?: string; code?: string; name?: string; nameTh?: string }>
    effectiveFrom?: string
    effectiveTo?: string | null
}

export interface TenantRuleScope {
    tenantId?: string
    huayId?: string
    huayName?: string
    huayTypeId?: string
    huayTypeName?: string
}

export interface TenantRulesGuideContent {
    rules: TenantRuleContentItem[]
    guides: TenantRuleContentItem[]
}

function extractScopedIds(items?: Array<string | { _id?: string }>): string[] {
    if (!Array.isArray(items)) return []
    return items
        .map((item) => {
            if (typeof item === 'string') return item
            if (item && typeof item === 'object' && typeof item._id === 'string') return item._id
            return ''
        })
        .filter(Boolean)
}

function isRuleEffectiveNow(rule: TenantRuleContentItem): boolean {
    const now = Date.now()
    const effectiveFrom = rule.effectiveFrom ? new Date(rule.effectiveFrom).getTime() : null
    const effectiveTo = rule.effectiveTo ? new Date(rule.effectiveTo).getTime() : null

    if (effectiveFrom && !Number.isNaN(effectiveFrom) && effectiveFrom > now) return false
    if (effectiveTo && !Number.isNaN(effectiveTo) && effectiveTo < now) return false
    return rule.isActive !== false
}

function isRuleApplicableToScope(rule: TenantRuleContentItem, scope: TenantRuleScope): boolean {
    if (!isRuleEffectiveNow(rule)) return false

    const relatedHuayTypeIds = extractScopedIds(rule.relatedHuayTypes)
    const relatedHuayIds = extractScopedIds(rule.relatedHuays)
    const hasHuayTypeScope = relatedHuayTypeIds.length > 0 || rule.applyToAllHuayTypes === true
    const hasHuayScope = relatedHuayIds.length > 0 || rule.applyToAllHuays === true

    if (rule.selectionMode === 'huay' || hasHuayScope) {
        if (!scope.huayId) return rule.applyToAllHuays === true
        return rule.applyToAllHuays === true || relatedHuayIds.includes(scope.huayId)
    }

    if (rule.selectionMode === 'type' || hasHuayTypeScope) {
        if (!scope.huayTypeId) return rule.applyToAllHuayTypes === true
        return rule.applyToAllHuayTypes === true || relatedHuayTypeIds.includes(scope.huayTypeId)
    }

    return true
}

function sortTenantRuleItems(items: TenantRuleContentItem[]): TenantRuleContentItem[] {
    return [...items].sort((left, right) => {
        const leftPriority = Number(left.priority || 0)
        const rightPriority = Number(right.priority || 0)
        if (leftPriority !== rightPriority) return rightPriority - leftPriority

        const leftDisplayOrder = Number(left.displayOrder || 0)
        const rightDisplayOrder = Number(right.displayOrder || 0)
        if (leftDisplayOrder !== rightDisplayOrder) return leftDisplayOrder - rightDisplayOrder

        return String(left.title || left.code || '').localeCompare(String(right.title || right.code || ''))
    })
}

export interface UserSlipsResponse {
    success: boolean
    data: { slips: BetSlip[]; total: number; customerCancelWindowMinutes?: number }
    error?: string
}

export interface SlipDetailResponse {
    success: boolean
    data: BetSlip
    error?: string
}

/** คืนค่า "วันนี้" ตามเวลาไทย (Asia/Bangkok) เพื่อให้ตรงกับ backend/Mongo ที่ใช้ Bangkok time */
function getTodayBangkokRange(): { todayStartBkk: Date; todayEndBkk: Date } {
    const formatter = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit' })
    const parts = formatter.formatToParts(new Date())
    const y = parts.find(p => p.type === 'year')!.value
    const m = parts.find(p => p.type === 'month')!.value
    const d = parts.find(p => p.type === 'day')!.value
    const todayStartBkk = new Date(`${y}-${m}-${d}T00:00:00+07:00`)
    const todayEndBkk = new Date(`${y}-${m}-${d}T23:59:59.999+07:00`)
    return { todayStartBkk, todayEndBkk }
}

class LotteryService {
    private baseUrl: string
    private tenantId: string

    constructor() {
        this.baseUrl = getApiBaseUrl()
        this.tenantId = getOptionalTenantId() ||
                        ((import.meta.env as any).PROD
                          ? ''
                          : ((import.meta.env as any).VITE_TENANT_ID ||
                             (import.meta.env as any).VITE_FALLBACK_TENANT_ID ||
                             'test-demo-01'))
    }

    async getCustomerSlipCancelWindowMinutes(): Promise<number> {
        if (customerSlipCancelWindowPromise) {
            return customerSlipCancelWindowPromise
        }

        const authToken = localStorage.getItem('accessToken')
        const tenantId = this.getEffectiveTenantId()
        const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/rules-and-regulations/${CUSTOMER_SLIP_CANCEL_RULE_CODE}`

        customerSlipCancelWindowPromise = fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken || ''}`,
                'X-Tenant-ID': tenantId
            }
        })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }
                const result = await response.json().catch(() => ({} as { data?: { settings?: { slipCancelWindowMinutes?: number } } }))
                const minutes = Number(result?.data?.settings?.slipCancelWindowMinutes)
                if (Number.isFinite(minutes) && minutes > 0) {
                    customerSlipCancelWindowCache = minutes
                }
                return customerSlipCancelWindowCache
            })
            .catch(() => customerSlipCancelWindowCache)
            .finally(() => {
                customerSlipCancelWindowPromise = null
            })

        return customerSlipCancelWindowPromise
    }

    async getTenantRulesAndGuides(scope: TenantRuleScope = {}): Promise<TenantRulesGuideContent> {
        const tenantId = scope.tenantId || this.getEffectiveTenantId()
        const huayTypeId = String(scope.huayTypeId || '').trim()
        const cacheKey = `${tenantId}:${huayTypeId || 'all'}`
        const cached = tenantRulesCache.get(cacheKey)

        let rulesPromise: Promise<TenantRuleContentItem[]>
        if (cached && Date.now() - cached.at < TENANT_RULES_CACHE_TTL_MS) {
            rulesPromise = Promise.resolve(cached.data)
        } else if (tenantRulesPromiseCache.has(cacheKey)) {
            rulesPromise = tenantRulesPromiseCache.get(cacheKey)!
        } else {
            const authToken = localStorage.getItem('accessToken')
            const params = new URLSearchParams()
            params.set('isActive', 'true')
            if (huayTypeId) params.set('huayTypeId', huayTypeId)
            const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/rules-and-regulations?${params.toString()}`

            rulesPromise = fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken || ''}`,
                    'X-Tenant-ID': tenantId
                }
            })
                .then(async (response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`)
                    }

                    const result = await response.json().catch(() => ({} as { data?: TenantRuleContentItem[] }))
                    const data = Array.isArray(result?.data) ? result.data : []
                    tenantRulesCache.set(cacheKey, { at: Date.now(), data })
                    return data
                })
                .catch(() => [])
                .finally(() => {
                    tenantRulesPromiseCache.delete(cacheKey)
                })

            tenantRulesPromiseCache.set(cacheKey, rulesPromise)
        }

        const allItems = await rulesPromise
        const applicableItems = sortTenantRuleItems(
            allItems.filter((item) => isRuleApplicableToScope(item, scope))
        )

        return {
            rules: applicableItems.filter((item) => String(item.code || '').startsWith('RULE_')),
            guides: applicableItems.filter((item) => String(item.code || '').startsWith('GUIDE_'))
        }
    }

    /** Called by App.tsx once the tenant is resolved from domain-mappings */
    setTenantId(id: string) {
        if (id && id !== this.tenantId) {
            this.tenantId = id
        }
    }

    /** Tenant ID to use in URLs — never empty (avoids /tenant//... and 404) */
    private getEffectiveTenantId(): string {
        if (this.tenantId && this.tenantId.trim()) {
            return this.tenantId
        }

        const runtimeTenantId = getOptionalTenantId()
        if (runtimeTenantId) {
            this.tenantId = runtimeTenantId
            return runtimeTenantId
        }

        const fallback = (import.meta.env as any).VITE_TENANT_ID ||
            (import.meta.env as any).VITE_FALLBACK_TENANT_ID ||
            'test-demo-01'
        return import.meta.env.PROD ? '' : fallback
    }

    private async delay(ms: number): Promise<void> {
        await new Promise((resolve) => setTimeout(resolve, ms))
    }

    private async fetchPublicTenantResource(
        paths: string[],
        headers: HeadersInit,
        options?: { retryStatuses?: number[]; maxAttempts?: number }
    ): Promise<{ response: Response; resolvedPath: string }> {
        const retryStatuses = options?.retryStatuses ?? [502, 503]
        const maxAttempts = options?.maxAttempts ?? 2

        let lastResponse: Response | null = null
        let lastResolvedPath = paths[0]

        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            for (const path of paths) {
                const response = await fetch(`${this.baseUrl}${path}`, {
                    method: 'GET',
                    headers,
                })

                lastResponse = response
                lastResolvedPath = path

                if (response.ok) {
                    return { response, resolvedPath: path }
                }

                // ถ้า route ถูก strip prefix โดย gateway ให้ลองอีก path ทันที
                if (response.status === 404) {
                    continue
                }

                // 502/503 มักเป็น route/service แกว่งชั่วคราว ลอง alternate path ในรอบเดียวกันก่อน
                if (retryStatuses.includes(response.status)) {
                    continue
                }

                return { response, resolvedPath: path }
            }

            if (attempt < maxAttempts - 1) {
                await this.delay(300 * (attempt + 1))
            }
        }

        if (!lastResponse) {
            throw new Error('No response received from public lottery API')
        }

        return { response: lastResponse, resolvedPath: lastResolvedPath }
    }

    /**
     * Get today's lottery rounds (all statuses)
     */
    async getTodayRounds(cacheBust = false): Promise<LotteryRound[]> {
        try {
            const authToken = localStorage.getItem('accessToken')
            const tenantId = this.getEffectiveTenantId()
            const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/rounds?limit=1000&forToday=1&timezone=Asia/Bangkok${cacheBust ? `&_t=${Date.now()}` : ''}`

            const response = await fetch(
                url,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken || ''}`,
                        'X-Tenant-ID': tenantId
                    },
                    ...(cacheBust && { cache: 'no-store' as RequestCache })
                }
            )

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result: LotteryRoundsResponse = await response.json()

            if (result.success && result.data) {
                // ใช้ "วันนี้" ตามเวลาไทย (Asia/Bangkok) เสมอ เพื่อให้ตรงกับ backend ที่สร้างรอบด้วย Bangkok time และ Mongo เก็บ UTC
                const { todayStartBkk, todayEndBkk } = getTodayBangkokRange()

                const filtered = result.data.filter(round => {
                    const betStart = new Date(round.betStartTime)
                    const betEnd = new Date(round.betEndTime)
                    // รอบอยู่ใน "วันนี้" (ไทย) ถ้า [betStart, betEnd] ทับกับ [todayStartBkk, todayEndBkk]
                    const overlapsToday = betEnd > todayStartBkk && betStart < todayEndBkk
                    if (!overlapsToday) return false
                    // รอบที่ปิดรับเที่ยงคืนวันนี้ (00:00) เป็นรอบสุดท้ายของเมื่อวาน ไม่นับเป็นวันนี้ (หวยรายชั่วโมง)
                    const betEndIsMidnightBkk = Math.abs(betEnd.getTime() - todayStartBkk.getTime()) < 60000
                    const isYesterdayLastRound = betEndIsMidnightBkk && betStart.getTime() < todayStartBkk.getTime()
                    return !isYesterdayLastRound
                })

                if (process.env.NODE_ENV === 'development' && filtered.length > 0) {
                  console.log(`📊 Total filtered rounds: ${filtered.length}`)
                }
                return filtered
            }

            return []
        } catch (error) {
            console.error('Error fetching today rounds:', error)
            return []
        }
    }

    /**
     * Get rounds by huayTypeId
     */
    async getRoundsByType(huayTypeId: string): Promise<LotteryRound[]> {
        try {
            const authToken = localStorage.getItem('accessToken')
            const tenantId = this.getEffectiveTenantId()

            const response = await fetch(
                `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/rounds?huayTypeId=${huayTypeId}&limit=1000`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken || ''}`,
                        'X-Tenant-ID': tenantId
                    }
                }
            )

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result: LotteryRoundsResponse = await response.json()
            return result.success && result.data ? result.data : []
        } catch (error) {
            console.error(`Error fetching rounds for type ${huayTypeId}:`, error)
            return []
        }
    }

    /**
     * Get tenant huay types — ตรงกับ announce-results ใช้กรองรอบตามประเภทหวย
     * ใช้ Public API (ไม่ต้อง login) เพื่อให้ผู้เข้าเว็บดูได้
     */
    async getTenantHuayTypes(options?: { limit?: number }): Promise<PublicHuayType[]> {
        const limit = options?.limit ?? 100
        const tid = this.getEffectiveTenantId()
        const pathWithLottery = `/api/v1/lottery/public/tenant/${tid}/huay-types?limit=${limit}`
        const pathWithoutLottery = `/api/v1/public/tenant/${tid}/huay-types?limit=${limit}`
        const headers = {
            'Content-Type': 'application/json',
            'X-Tenant-ID': tid
        }
        const { response, resolvedPath } = await this.fetchPublicTenantResource(
            [pathWithLottery, pathWithoutLottery],
            headers
        )
        if (!response.ok) {
            console.warn('[lotteryService] getTenantHuayTypes HTTP', response.status, this.baseUrl + resolvedPath)
            return []
        }
        const result = await response.json() as { success?: boolean; data?: unknown }
        if (!result.success || !result.data) return []
        const raw = result.data
        const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && Array.isArray((raw as any).data) ? (raw as any).data : [])
        return list.map((ht: any) => ({
            _id: ht._id ?? ht.huayTypeId ?? '',
            huayTypeId: ht.huayTypeId ?? ht._id,
            name: ht.name ?? '',
            code: ht.code,
            imageUrl: ht.imageUrl ?? null,
            imageURL: ht.imageURL ?? null,
            configuration: ht.configuration ? { isActive: ht.configuration.isActive } : undefined
        }))
    }

    /**
     * Get lottery rounds — Public API เท่านั้น (ไม่ต้อง login)
     * ใช้ดูผลหวยได้ทั้งก่อน/หลัง login
     * - ดึงแค่วันต่อวัน: ส่ง dateFrom, dateTo = วันที่เลือก (เช่น 2025-02-05), limit ประมาณ 200
     * - ดึง round เดียว: ส่ง roundId, limit=1
     * - ไม่ส่ง date/roundId: ดึงทั้งหมด (page, limit) — ใช้เฉพาะเมื่อจำเป็น
     */
    async getLotteryRounds(options?: {
        page?: number
        limit?: number
        dateFrom?: string
        dateTo?: string
        roundId?: string
        timezone?: string
    }): Promise<LotteryRound[]> {
        const params = new URLSearchParams()
        params.set('page', String(options?.page ?? 1))
        params.set('limit', String(options?.limit ?? 200))
        if (options?.dateFrom) params.set('dateFrom', options.dateFrom)
        if (options?.dateTo) params.set('dateTo', options.dateTo)
        if (options?.roundId) params.set('roundId', options.roundId)
        if (options?.timezone) params.set('timezone', options.timezone)
        params.set('includePrizeDetails', 'false')
        params.set('includeSummaryStats', 'false')
        params.set('includeRoundLimits', 'false')
        const tid = this.getEffectiveTenantId()
        const pathWithLottery = `/api/v1/lottery/public/tenant/${tid}/lottery-rounds?${params}`
        const pathWithoutLottery = `/api/v1/public/tenant/${tid}/lottery-rounds?${params}`
        const headers = { 'Content-Type': 'application/json', 'X-Tenant-ID': tid }
        const { response, resolvedPath } = await this.fetchPublicTenantResource(
            [pathWithLottery, pathWithoutLottery],
            headers
        )
        if (!response.ok) {
            const errText = await response.text()
            console.error('[lotteryService] getLotteryRounds HTTP', response.status, this.baseUrl + resolvedPath, errText.slice(0, 200))
            const msg =
                response.status === 502
                    ? '502 Bad Gateway — Lottery Service ไม่ตอบหรือ Load Balancer ชี้ผิด'
                    : response.status === 503
                        ? '503 Service Unavailable — Backend ยังไม่พร้อม'
                        : response.status === 404
                            ? '404 Not Found — path API ไม่มีใน Backend'
                            : `API error ${response.status}: ${response.statusText}`
            throw new Error(msg)
        }
        const result = await response.json() as { success?: boolean; data?: unknown }
        if (!result.success) {
            console.error('[lotteryService] getLotteryRounds success=false', result)
            return []
        }
        const raw = result.data
        const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && Array.isArray((raw as any).data) ? (raw as any).data : [])
        return (list as LotteryRound[]).slice(0, options?.limit ?? 200)
    }

    /**
     * Get recent rounds (no date filter) — ดึงรอบล่าสุดทั้งหมดในระบบ
     */
    async getRoundsRecent(limit = 500): Promise<LotteryRound[]> {
        const authToken = localStorage.getItem('accessToken')
        const tenantId = this.getEffectiveTenantId()
        const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/rounds?limit=${limit}`
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken || ''}`,
                'X-Tenant-ID': tenantId
            }
        })
        if (!response.ok) {
            const errText = await response.text()
            console.error('[lotteryService] getRoundsRecent HTTP', response.status, url, errText.slice(0, 200))
            throw new Error(`API error ${response.status}: ${response.statusText}`)
        }
        const result = await response.json() as { success?: boolean; data?: unknown }
        if (!result.success) {
            console.error('[lotteryService] getRoundsRecent success=false', result)
            return []
        }
        const raw = result.data
        const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && Array.isArray((raw as any).data) ? (raw as any).data : [])
        return (list as LotteryRound[]).slice(0, limit)
    }

    /**
     * Get rounds that overlap a date range (สำหรับผลรางวัล: เลือกรอบที่ specific date อยู่ใน [betStart, betEnd] หรือดึงรอบล่าสุด)
     * Backend: betEnd >= dateFrom 00:00 และ betStart <= dateTo 23:59 (Bangkok)
     */
    async getRoundsByDateRange(dateFrom: string, dateTo: string, limit = 200): Promise<LotteryRound[]> {
        try {
            const authToken = localStorage.getItem('accessToken')
            const tenantId = this.getEffectiveTenantId()
            const params = new URLSearchParams({ dateFrom, dateTo, limit: String(limit), timezone: 'Asia/Bangkok' })
            const response = await fetch(
                `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/rounds?${params}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken || ''}`,
                        'X-Tenant-ID': tenantId
                    }
                }
            )
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
            const result = await response.json() as { success?: boolean; data?: unknown }
            if (!result.success) return []
            const raw = result.data
            const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && Array.isArray((raw as any).data) ? (raw as any).data : (raw && typeof raw === 'object' && Array.isArray((raw as any).items) ? (raw as any).items : []))
            return list as LotteryRound[]
        } catch (error) {
            console.error('Error fetching rounds by date range:', error)
            return []
        }
    }

    /**
     * Group rounds by huayTypeName
     */
    groupRoundsByType(rounds: LotteryRound[]): Record<string, LotteryRound[]> {
        return rounds.reduce((acc, round) => {
            const typeName = round.huayTypeName || 'อื่นๆ'
            if (!acc[typeName]) {
                acc[typeName] = []
            }
            acc[typeName].push(round)
            return acc
        }, {} as Record<string, LotteryRound[]>)
    }

    sortRoundsForLottoDisplay(rounds: LotteryRound[]): LotteryRound[] {
        return [...rounds].sort(compareRoundsForLottoDisplay)
    }

    /**
     * Calculate time remaining until bet close
     */
    getTimeRemaining(betEndTime: string): { hours: number; minutes: number; seconds: number; isExpired: boolean } {
        const now = new Date().getTime()
        const endTime = new Date(betEndTime).getTime()
        const diff = endTime - now

        if (diff <= 0) {
            return { hours: 0, minutes: 0, seconds: 0, isExpired: true }
        }

        const hours = Math.floor(diff / (1000 * 60 * 60))
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((diff % (1000 * 60)) / 1000)

        return { hours, minutes, seconds, isExpired: false }
    }

    /**
     * Format time remaining as string
     */
    formatTimeRemaining(betEndTime: string): string {
        const { hours, minutes, seconds, isExpired } = this.getTimeRemaining(betEndTime)

        if (isExpired) {
            return 'ปิดรับแทง'
        }

        if (hours > 24) {
            const days = Math.floor(hours / 24)
            return `${days} วัน ${hours % 24}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        }

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }

    /**
     * Get round detail by round ID
     */
    async getRoundById(roundId: string): Promise<LotteryRound | null> {
        try {
            const authToken = localStorage.getItem('accessToken')
            const tenantId = this.getEffectiveTenantId()
            const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/rounds?roundId=${encodeURIComponent(roundId)}&limit=1`

            const response = await fetch(url, {
                method: 'GET',
                cache: 'no-store',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken || ''}`,
                    'X-Tenant-ID': tenantId
                }
            })

            if (!response.ok) {
                return null
            }

            const result: LotteryRoundsResponse = await response.json()

            if (result.success && Array.isArray(result.data) && result.data.length > 0) {
                return result.data[0]
            }

            return null
        } catch (error) {
            console.error(`Error fetching round ${roundId}:`, error)
            return null
        }
    }

    /**
     * Validate bet items (ตรวจสอบเลขเต็ม/อั้นจ่าย)
     */
    async validateBetItems(
        roundId: string,
        packageCode: string | null,
        betItems: Array<{ prizeTypeCode: string; number: string; amount: number }>,
        options: { signal?: AbortSignal } = {}
    ): Promise<any> {
        try {
            const authToken = localStorage.getItem('accessToken')
            const tenantId = this.getEffectiveTenantId()
            const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/validate-bet`

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken || ''}`,
                    'X-Tenant-ID': tenantId
                },
                body: JSON.stringify({
                    roundId,
                    packageCode,
                    betItems
                }),
                signal: options.signal
            })

            if (!response.ok) {
                const error = await response.json()
                throw new Error(error.message || 'Validation failed')
            }

            const result = await response.json()
            return result
        } catch (error: any) {
            console.error('Error validating bet items:', error)
            throw error
        }
    }

    /**
     * Place bet (ส่งโพย)
     */
    async placeBet(
        userId: string,
        roundId: string,
        packageCode: string | null,
        betItems: Array<{ prizeTypeCode: string; number: string; amount: number }>,
        source: string = 'web',
        username?: string
    ): Promise<any> {
        try {
            const authToken = localStorage.getItem('accessToken')
            const tenantId = this.getEffectiveTenantId()
            const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/bet`
            const body: Record<string, unknown> = {
                userId,
                roundId,
                packageCode,
                betItems,
                source,
                userAgent: navigator.userAgent
            }
            if (username != null && username !== '') body.username = username

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken || ''}`,
                    'X-Tenant-ID': tenantId
                },
                body: JSON.stringify(body)
            })

            const responseBody = await parseResponseBody(response)

            if (!response.ok) {
                const message = resolvePlaceBetErrorMessage(response, responseBody)
                const errorPayload = responseBody.data && typeof responseBody.data === 'object'
                    ? {
                        ...(responseBody.data as Record<string, unknown>),
                        message
                    }
                    : {
                        message,
                        status: response.status,
                        rawText: responseBody.text
                    }

                const apiError = new Error(message) as Error & {
                    response?: { data?: unknown }
                    details?: unknown
                }
                apiError.response = { data: errorPayload }
                apiError.details = errorPayload
                throw apiError
            }

            if (!responseBody.data || typeof responseBody.data !== 'object') {
                throw new Error('รูปแบบข้อมูลตอบกลับจากระบบไม่ถูกต้อง')
            }

            return responseBody.data
        } catch (error: any) {
            console.error('Error placing bet:', error)
            throw error
        }
    }

    /**
     * Get user bet slips (โพยที่ซื้อ)
     * GET /api/v1/lottery/tenant/:tenantId/bet/user/:userId
     */
    async getUserSlips(
        userId: string,
        options?: {
            limit?: number
            skip?: number
            status?: string
            roundId?: string
            isSettled?: boolean
            startDate?: string
            endDate?: string
            summary?: boolean
        }
    ): Promise<{ slips: BetSlip[]; total: number }> {
        try {
            const authToken = localStorage.getItem('accessToken')
            const tenantId = this.getEffectiveTenantId()
            const params = new URLSearchParams()
            if (options?.limit != null) params.set('limit', String(options.limit))
            if (options?.skip != null) params.set('skip', String(options.skip))
            if (options?.status) params.set('status', options.status)
            if (options?.roundId) params.set('roundId', options.roundId)
            if (options?.isSettled !== undefined) params.set('isSettled', String(options.isSettled))
            if (options?.startDate) params.set('startDate', options.startDate)
            if (options?.endDate) params.set('endDate', options.endDate)
            if (options?.summary) params.set('summary', 'true')
            const qs = params.toString()
            const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/bet/user/${encodeURIComponent(userId)}${qs ? `?${qs}` : ''}`
            const shouldRetry = (status: number) => status === 502 || status === 503 || status === 504

            for (let attempt = 0; attempt < 2; attempt += 1) {
                try {
                    const response = await fetch(url, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${authToken || ''}`,
                            'X-Tenant-ID': tenantId,
                            'X-Timeout': '30000'
                        }
                    })

                    if (!response.ok) {
                        if (attempt === 0 && shouldRetry(response.status)) {
                            await new Promise((resolve) => setTimeout(resolve, 350))
                            continue
                        }
                        throw new Error(`HTTP error! status: ${response.status}`)
                    }

                    const result: UserSlipsResponse = await response.json()
                    if (result.success && result.data) {
                        const customerCancelWindowMinutes = Number(result.data.customerCancelWindowMinutes)
                        const slips = (result.data.slips || []).map((slip) => ({
                            ...slip,
                            customerCancelWindowMinutes: Number.isFinite(customerCancelWindowMinutes) && customerCancelWindowMinutes > 0
                                ? customerCancelWindowMinutes
                                : Number(slip.customerCancelWindowMinutes) || CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES
                        }))
                        return { slips, total: result.data.total ?? slips.length }
                    }
                    return { slips: [], total: 0 }
                } catch (error: any) {
                    if (attempt === 0) {
                        const status = Number(error?.status || error?.response?.status || 0)
                        if (!status || shouldRetry(status)) {
                            await new Promise((resolve) => setTimeout(resolve, 350))
                            continue
                        }
                    }
                    throw error
                }
            }

            return { slips: [], total: 0 }
        } catch (error: any) {
            console.error('Error fetching user slips:', error)
            return { slips: [], total: 0 }
        }
    }

    /**
     * Get single bet slip by slipId or _id
     * GET /api/v1/lottery/tenant/:tenantId/bet/slip/:slipId
     */
    async getSlip(slipId: string): Promise<BetSlip | null> {
        try {
            const authToken = localStorage.getItem('accessToken')
            const tenantId = this.getEffectiveTenantId()
            const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/bet/slip/${encodeURIComponent(slipId)}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken || ''}`,
                    'X-Tenant-ID': tenantId
                }
            })

            if (!response.ok) {
                if (response.status === 404) return null
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result: SlipDetailResponse = await response.json()
            if (result.success && result.data) {
                return {
                    ...result.data,
                    customerCancelWindowMinutes: Number(result.data.customerCancelWindowMinutes) || CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES
                }
            }
            return null
        } catch (error: any) {
            console.error('Error fetching slip:', error)
            return null
        }
    }

    async cancelSlip(
        slipId: string,
        reason = 'User cancelled'
    ): Promise<{ success: boolean; message?: string; data?: unknown }> {
        try {
            const authToken = localStorage.getItem('accessToken')
            const tenantId = this.getEffectiveTenantId()
            const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/bet/slip/${encodeURIComponent(slipId)}/cancel`

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken || ''}`,
                    'X-Tenant-ID': tenantId
                },
                body: JSON.stringify({ reason })
            })

            const result = await response.json().catch(() => ({} as { success?: boolean; message?: string; data?: unknown }))
            if (!response.ok || !result.success) {
                return {
                    success: false,
                    message: result.message || `HTTP error! status: ${response.status}`
                }
            }

            return {
                success: true,
                message: result.message,
                data: result.data
            }
        } catch (error: any) {
            console.error('Error cancelling slip:', error)
            return {
                success: false,
                message: error?.message || 'ไม่สามารถคืนโพยได้'
            }
        }
    }

    async cancelBetItem(
        slipId: string,
        itemIndex: number,
        reason = 'User cancelled bet item'
    ): Promise<{ success: boolean; message?: string; data?: unknown }> {
        try {
            const authToken = localStorage.getItem('accessToken')
            const tenantId = this.getEffectiveTenantId()
            const url = `${this.baseUrl}/api/v1/lottery/tenant/${tenantId}/bet/slip/${encodeURIComponent(slipId)}/item/${encodeURIComponent(String(itemIndex))}/cancel`

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken || ''}`,
                    'X-Tenant-ID': tenantId
                },
                body: JSON.stringify({ reason })
            })

            const result = await response.json().catch(() => ({} as { success?: boolean; message?: string; data?: unknown }))
            if (!response.ok || !result.success) {
                return {
                    success: false,
                    message: result.message || `HTTP error! status: ${response.status}`
                }
            }

            return {
                success: true,
                message: result.message,
                data: result.data
            }
        } catch (error: any) {
            console.error('Error cancelling bet item:', error)
            return {
                success: false,
                message: error?.message || 'ไม่สามารถคืนเลขได้'
            }
        }
    }
}

export const lotteryService = new LotteryService()
export default lotteryService

